import {
  ConnectionState,
  eStreamConnectionStateAtom,
  EStreamTradeDTO,
  LoadableResourceLoaded,
  LoadableResourceLoading,
} from '@eih-netbroker/common';
import { set } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { Config } from '../../../shared/config/config';
import { betMarketCodes } from '../../../shared/config/markets';
import { instrumentStore } from '../../../shared/data/instrumentStore';
import { useEStream } from '../../../shared/services/sse/useEStream';
import { createLogger } from '../../../shared/utils/logger';
import {
  addNewTrades,
  mapEStreamTradeToBETFillListItem,
  mapTradeHistoryListToBETFillList,
} from '../services/bet/betFillListMapper';
import { BETFillListItemChange, betTradesAtom } from '../states/betTradesAtom';

export type BETFillListLastPrices = Record<string, undefined | number>;

export type BETFillList =
  | LoadableResourceLoading
  | (LoadableResourceLoaded & {
      trades: Array<BETFillListItem>;
      lastReload: number;
    });

export type BETFillListItemWithoutChangeDirection = {
  id: string;
  ticker: string;
  price: number;
  quantity: number;
  time: Date;
  tradeId: string;
};
export type BETFillListItem = BETFillListItemWithoutChangeDirection & {
  change: BETFillListItemChange;
};

export type BETFillListContextProps =
  | undefined
  | {
      loadBETFillList: () => Promise<void>;
    };

export const BETFillListContext = createContext<BETFillListContextProps>(undefined);

export const BETFillListProvider = ({ children }: { children: React.ReactNode }) => {
  const { getBETTrades } = useEStream();
  const logger = useRef(createLogger('BETFillListProvider'));
  const setBetTrades = useSetAtom(betTradesAtom);
  const tradeIds = useRef(new Set<string>());
  const isBETTradesLoading = useRef(false);
  const eStreamConnectionState = useAtomValue(eStreamConnectionStateAtom);
  const [previousConnectionState, setPreviousConnectionState] = useState<ConnectionState | undefined>(undefined);
  const tradesWhileLoading = useRef<Array<EStreamTradeDTO>>([]);
  const lastPrices = useRef<BETFillListLastPrices>({});

  // Loads the rest of the trades page by page
  const asyncLoadAllBetTradesPerPage = useCallback(
    async (prevLastTradeMetaId: string): Promise<void> => {
      const today = set(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
      const tradesResp = await getBETTrades(today, Config.NetBrokerWeb.BETListTradeLimitPerPage, prevLastTradeMetaId);
      const trades = mapTradeHistoryListToBETFillList(tradesResp.data, lastPrices.current);
      trades.forEach(trade => tradeIds.current.add(trade.id));

      setBetTrades(current => ({
        ...current,
        trades: current.isLoading ? [] : [...current.trades, ...trades],
      }));

      const currentlastTradeMetaId = tradesResp.meta && tradesResp.meta[0].pagination?.lastid;

      if (trades.length >= Config.NetBrokerWeb.BETListTradeLimitPerPage && currentlastTradeMetaId) {
        asyncLoadAllBetTradesPerPage(currentlastTradeMetaId);
      } else {
        isBETTradesLoading.current = false;
      }
    },
    [getBETTrades, setBetTrades],
  );

  // First loads the first X number of trades then loads the rest async
  const loadBETFillList = useCallback(
    async (isInit?: boolean): Promise<void> => {
      if (isBETTradesLoading.current && !isInit) {
        return;
      }
      isBETTradesLoading.current = true;
      try {
        const today = set(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
        const tradesResp = await getBETTrades(today, Config.NetBrokerWeb.BETListTradeLimitPerPage);
        const trades = mapTradeHistoryListToBETFillList(tradesResp.data, lastPrices.current);
        trades.forEach(trade => tradeIds.current.add(trade.id));

        setBetTrades({
          isLoading: false,
          trades,
          lastReload: Date.now(),
        });

        const lastTradeMetaId = tradesResp.meta && tradesResp.meta[0].pagination?.lastid;

        if (trades.length >= Config.NetBrokerWeb.BETListTradeLimitPerPage && lastTradeMetaId) {
          asyncLoadAllBetTradesPerPage(lastTradeMetaId);
        } else {
          isBETTradesLoading.current = false;
        }
      } catch (err) {
        isBETTradesLoading.current = false;
      }
    },
    [getBETTrades, setBetTrades, asyncLoadAllBetTradesPerPage],
  );

  const updateBetTradesFromEStream = useCallback(
    (trades: Array<EStreamTradeDTO>) => {
      const newBetTrades = mapEStreamTradeToBETFillListItem(trades);
      if (newBetTrades.length > 0) {
        setBetTrades(prevState =>
          addNewTrades(prevState, newBetTrades, tradeIds.current, lastPrices.current, logger.current),
        );
      }
    },
    [setBetTrades],
  );

  /**
   * Save trades from eStream into the atom
   */
  useEffect(() => {
    if (!isBETTradesLoading.current && tradesWhileLoading.current.length > 0) {
      updateBetTradesFromEStream(tradesWhileLoading.current);
      tradesWhileLoading.current = [];
    }

    return instrumentStore.registerTrades(betMarketCodes, (trades: Array<EStreamTradeDTO>) => {
      if (isBETTradesLoading.current) {
        tradesWhileLoading.current.push(...trades);
      } else {
        updateBetTradesFromEStream(trades);
      }
    });
  }, [updateBetTradesFromEStream]);

  useEffect(() => {
    if (
      eStreamConnectionState === ConnectionState.Connected &&
      previousConnectionState !== ConnectionState.Connected &&
      previousConnectionState !== undefined
    ) {
      loadBETFillList();
    }
    setPreviousConnectionState(eStreamConnectionState);
  }, [eStreamConnectionState, loadBETFillList, previousConnectionState]);

  useEffect(() => {
    if (isBETTradesLoading.current) {
      return;
    }
    isBETTradesLoading.current = true;
    loadBETFillList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Init bet list on load
  }, []);

  return <BETFillListContext.Provider value={{ loadBETFillList }}>{children}</BETFillListContext.Provider>;
};
